import { createEvent, createStore } from 'effector'

export interface MessageSendingProgress {
  totalMessages: number
  sentMessages: number
  isSending: boolean
  error?: string
}

export interface SuggestionSendingState {
  [suggestionIndex: number]: MessageSendingProgress
}

export const startSendingMessages = createEvent<{
  fanId: number
  version: number
  suggestionIndex: number
  totalMessages: number
}>('start sending messages')

export const updateSendingProgress = createEvent<{
  fanId: number
  version: number
  suggestionIndex: number
  sentMessages: number
}>('update sending progress')

export const clearSuggestionSendingState = createEvent<{
  fanId: number
  version: number
  suggestionIndex: number
}>('clear suggestion sending state')

export const clearAllSendingStates = createEvent('clear all sending states')

export const $messageSendingStates = createStore<
  Record<string, SuggestionSendingState>
>(
  {},
  {
    name: 'message sending states',
  },
)

const createStateKey = (fanId: number, version: number) => `${fanId}-${version}`

$messageSendingStates
  .on(
    startSendingMessages,
    (state, { fanId, version, suggestionIndex, totalMessages }) => {
      const key = createStateKey(fanId, version)
      const currentState = state[key] || {}

      return {
        ...state,
        [key]: {
          ...currentState,
          [suggestionIndex]: {
            totalMessages,
            sentMessages: 0,
            isSending: true,
          },
        },
      }
    },
  )
  .on(
    updateSendingProgress,
    (state, { fanId, version, suggestionIndex, sentMessages }) => {
      const key = createStateKey(fanId, version)
      const currentState = state[key]

      if (!currentState || !currentState[suggestionIndex]) {
        return state
      }

      return {
        ...state,
        [key]: {
          ...currentState,
          [suggestionIndex]: {
            ...currentState[suggestionIndex],
            sentMessages,
          },
        },
      }
    },
  )
  .on(
    clearSuggestionSendingState,
    (state, { fanId, version, suggestionIndex }) => {
      const key = createStateKey(fanId, version)
      const currentState = state[key]

      if (!currentState) {
        return state
      }

      const newSuggestionState = { ...currentState }

      delete newSuggestionState[suggestionIndex]

      if (Object.keys(newSuggestionState).length === 0) {
        const newState = { ...state }

        delete newState[key]

        return newState
      }

      return {
        ...state,
        [key]: newSuggestionState,
      }
    },
  )
  .reset(clearAllSendingStates)

export const $isAnySendingForFan = (fanId: number) =>
  $messageSendingStates.map((states) => {
    const fanKeys = Object.keys(states).filter((key) =>
      key.startsWith(`${fanId}-`),
    )

    if (fanKeys.length === 0) return false

    return fanKeys.some((key) => {
      const fanState = states[key]

      return Object.values(fanState).some((progress) => progress.isSending)
    })
  })
